import { Message } from "../../types/chat";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeSanitize from "rehype-sanitize";
import { X } from "lucide-react";

import { LinkifiedText } from "./LinkifiedText";
import { YouTubeSmartPreview } from "./YouTubeSmartPreview";
import YouTubePlayer, { extractYouTubeId } from "../media/YouTubePlayer";

interface MessageBubbleProps {
  message: Message;
  onDelete?: () => void;
}

/** Strip YouTube URLs from visible text, keep one for the embed */
const YT_REGEX =
  /(https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)[\w\-]{11}[^\s)]*)/gi;

function stripYouTubeFromText(text: string) {
  if (!text) return { cleanText: "", youTubeUrls: [] as string[] };

  const youTubeUrls = Array.from(text.matchAll(YT_REGEX)).map((m) => m[0]);

  // Remove ALL YouTube URLs from the visible text
  const cleanText = text
    .replace(YT_REGEX, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return { cleanText, youTubeUrls };
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onDelete }) => {
  const isUser = message.sender === "user";
  const isSystem = message.sender === "system";

  // ---- System message special case ----
  if (isSystem) {
    return (
      <div className="mx-3 my-1 self-start max-w-[85%]">
        <div className="text-xs text-gray-500 italic">{message.text}</div>
      </div>
    );
  }

  // ---- Parse & sanitize YouTube links up front ----
  const { cleanText, youTubeUrls } = stripYouTubeFromText(message.text || "");
  const firstYouTubeUrl = youTubeUrls.find((u) => extractYouTubeId(u));

  // 🌿 Determine bubble color variant
  const variantClass =
    message.variant === "coachPrompt"
      ? "bg-emerald-50 text-emerald-900 border border-emerald-200 animate-fadeIn"
      : isUser
      ? "bg-blue-100 text-black"
      : "bg-gray-100 text-black";

  // ---- Normal bubbles (user / assistant) ----
  const BubbleContent = () => {
    // File upload special case
    if (message.text.startsWith("📎 File uploaded:")) {
      const fileName = message.text.replace("📎 File uploaded: ", "");
      return (
        <div className="flex items-center space-x-2">
          <span>📎</span>
          <span className="font-medium">{fileName}</span>
        </div>
      );
    }

    // Assistant message with markdown (feed CLEAN text)
    if (message.markdown && !isUser) {
      return (
        <div className="prose prose-sm max-w-none break-words">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            rehypePlugins={[rehypeSanitize as any]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer">
                  {props.children}
                </a>
              ),
            }}
          >
            {cleanText}
          </ReactMarkdown>
        </div>
      );
    }

    // Fallback: plain text (feed CLEAN text)
    return (
      <p className="whitespace-pre-wrap break-words">
        <LinkifiedText text={cleanText} />
      </p>
    );
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "relative rounded-2xl px-4 py-3 shadow max-w-[80%] sm:max-w-[70%]",
          variantClass,
        ].join(" ")}
      >
        {/* Delete button */}
        {onDelete && (
          <button
            onClick={onDelete}
            className="absolute top-1 right-1 p-1 rounded-full hover:bg-gray-300"
            title="Delete message"
          >
            <X className="w-3 h-3 text-gray-600" />
          </button>
        )}

        {/* 💡 Small icon for coachPrompt */}
        {message.variant === "coachPrompt" ? (
          <div className="flex items-start gap-2">
            <span className="text-emerald-500 mt-0.5">💡</span>
            <div className="flex-1">
              <BubbleContent />
            </div>
          </div>
        ) : (
          <BubbleContent />
        )}

        {/* 🎬 YouTube / smart preview */}
        {firstYouTubeUrl ? (
          <div className="mt-3">
            <YouTubePlayer url={firstYouTubeUrl} title={message.text} />
          </div>
        ) : (
          <YouTubeSmartPreview text={message.text} behavior="auto" />
        )}

        {/* 🖼️ Inline images */}
        {message.images?.length ? (
          <div className="mt-2 grid grid-cols-2 gap-2">
            {message.images.map((im, i) => (
              <img
                key={i}
                src={im.url}
                alt={im.alt || ""}
                className="w-full rounded-xl"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) =>
                  ((e.currentTarget as HTMLImageElement).style.display = "none")
                }
              />
            ))}
          </div>
        ) : null}

        {/* 🆕 Conditional quick-action bar placeholder */}
        {!message.hideQuick && (
          <div id="quick-action-bar" className="mt-3">
            {/* Existing quick-action component or buttons would mount here */}
            {/* Example: <QuickActions message={message} /> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
